import { useGetUserNameWithFallback } from '@pba/react-utils';
import type { ProgramEventListType } from 'models';
import { useCallback } from 'react';
import { Avatar, Col, Icon, Row, Space, Text, useFormatMembersCount } from 'ui';
import { useGetParticipantsCountLabel } from 'features/events';
import { AvatarLink, UserLink, UserPopover, useGetNodeUserAvatar } from 'features/users';

type EmailParticipant = {
  email: string;
  firstName?: string;
  id?: never;
  lastName?: string;
  type: 'email';
};

type P = {
  eventType: ProgramEventListType;
  numberOfParticipants?: number;
  participants?: Participant[];
};

type Participant = EmailParticipant | RegisteredParticipant;

type RegisteredParticipant = {
  avatar?: string;
  email?: string;
  firstName?: string;
  id: string;
  lastName?: string;
  type: 'user';
};

const Participants = ({ eventType, numberOfParticipants, participants }: P) => {
  const formattedParticipantsCount = useFormatMembersCount({ count: numberOfParticipants ?? 0 });
  const getAvatarUrl = useGetNodeUserAvatar();
  const getUserNameWithFallback = useGetUserNameWithFallback();
  const getParticipantsCountLabel = useGetParticipantsCountLabel({ eventType });

  const getParticipantAvatarUrl = useCallback(
    (participant: RegisteredParticipant) => getAvatarUrl({ size: 'medium', userId: participant.id }),
    [getAvatarUrl],
  );

  return (
    <Space className="participants" orientation="vertical" size="small">
      {numberOfParticipants === undefined ? null : (
        <Space size="small">
          <Icon color="text" icon="group-line" size="small" />
          <Text>{getParticipantsCountLabel(formattedParticipantsCount)}</Text>
        </Space>
      )}
      {participants ? (
        <Row gutter="xs">
          <Col>
            <Icon color="text" icon="group-line" size="small" />
          </Col>
          <Col>
            <Space orientation="vertical" size="middle">
              {participants.map((participant) => (
                <Row gutter="xs" key={participant.id}>
                  <Col>
                    {participant.id ? (
                      <UserPopover
                        fallback={{
                          ...participant,
                          firstName: participant.firstName,
                          lastName: participant.lastName,
                          profilePhoto: getParticipantAvatarUrl(participant),
                        }}
                        userId={participant.id}
                      >
                        <AvatarLink
                          avatar={{
                            size: 'xs',
                            src: getParticipantAvatarUrl(participant),
                          }}
                          userId={participant.id}
                        />
                      </UserPopover>
                    ) : (
                      <Avatar size="xs" />
                    )}
                  </Col>
                  <Col flex={1}>
                    {participant.id ? (
                      <UserPopover
                        fallback={{
                          ...participant,
                          firstName: participant.firstName,
                          lastName: participant.lastName,
                          profilePhoto: getParticipantAvatarUrl(participant),
                        }}
                        userId={participant.id}
                      >
                        <UserLink userId={participant.id}>
                          <Text weight="medium">{getUserNameWithFallback(participant)}</Text>
                        </UserLink>
                      </UserPopover>
                    ) : (
                      <Text weight="medium">{getUserNameWithFallback(participant)}</Text>
                    )}
                  </Col>
                </Row>
              ))}
            </Space>
          </Col>
        </Row>
      ) : null}
    </Space>
  );
};

export default Participants;
